/**
 * Shared canvas primitives for the matplotlib-equivalent report/QC figures
 * (`figures.ts`, `qc.ts`). Everything here reproduces one specific piece of
 * the native OpenADS figure stack so the PNGs line up with the Python ones:
 *
 *  - `axialSlice`: `np.rot90(vol[:, :, k])` on a RAS-canonical volume (every
 *    native figure loads through `nib.as_closest_canonical`, this app's
 *    `loadNiftiFile` already guarantees RAS — see nifti.ts).
 *  - `GRAY_LUT` / `JET_LUT` + `colorize`: matplotlib's 256-entry `gray`/`jet`
 *    colormaps and `Normalize` -> LUT index arithmetic (under -> lut[0],
 *    over -> lut[255], NaN -> transparent "bad" colour).
 *  - `percentile`: `np.percentile` default (linear) interpolation.
 *  - `findContours`: `skimage.measure.find_contours(img, level)` (marching
 *    squares on pixel centres, linear edge interpolation, segments chained
 *    into polylines so the `len(contour) > 5` filter in pwi_visualization
 *    can be honoured).
 *
 * No DOM dependency beyond a 2D canvas: OffscreenCanvas when available
 * (works on the main thread and in workers), `document.createElement`
 * otherwise.
 */
import type { NiftiVolume } from "../nifti";

/** matplotlib `savefig(dpi=150)` — every native report/QC PNG uses it. */
export const DPI = 150;
/** Points -> pixels at DPI (matplotlib linewidths/font sizes are in points). */
export const PT = DPI / 72;
/** Browser stand-in for matplotlib's default DejaVu Sans. */
export const FONT_FAMILY = '"DejaVu Sans", "Bitstream Vera Sans", Verdana, "Helvetica Neue", Arial, sans-serif';

export type Canvas2D = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;
export type AnyCanvas = OffscreenCanvas | HTMLCanvasElement;

export function createCanvas(width: number, height: number): AnyCanvas {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(w, h);
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

export function context2d(canvas: AnyCanvas): Canvas2D {
  const ctx = canvas.getContext("2d") as Canvas2D | null;
  if (!ctx) throw new Error("2D canvas context unavailable");
  return ctx;
}

export async function canvasToPng(canvas: AnyCanvas): Promise<Blob> {
  if ("convertToBlob" in canvas) return canvas.convertToBlob({ type: "image/png" });
  return new Promise((resolve, reject) =>
    (canvas as HTMLCanvasElement).toBlob((b) => (b ? resolve(b) : reject(new Error("canvas.toBlob failed"))), "image/png")
  );
}

// ---------------------------------------------------------------------------
// Slicing
// ---------------------------------------------------------------------------

/** A 2D image in display (row-major, row 0 = top) order. */
export interface Slice2D {
  width: number;
  height: number;
  data: Float32Array;
}

/**
 * `np.rot90(vol[:, :, k])` for an F-order RAS volume: display row i is
 * y = ny-1-i (anterior at the top), display column j is x.
 */
export function axialSlice(vol: NiftiVolume, k: number): Slice2D {
  const [nx, ny] = vol.dims;
  const out = new Float32Array(nx * ny);
  const base = k * nx * ny;
  for (let i = 0; i < ny; i++) {
    const src = base + (ny - 1 - i) * nx;
    out.set(vol.voxels.subarray(src, src + nx), i * nx);
  }
  return { width: nx, height: ny, data: out };
}

/** Sagittal slice at x: columns = y (posterior left -> anterior right), rows = z (superior at top). */
export function sagittalSlice(vol: NiftiVolume, x: number): Slice2D {
  const [nx, ny, nz] = vol.dims;
  const out = new Float32Array(ny * nz);
  for (let i = 0; i < nz; i++) {
    const z = nz - 1 - i;
    for (let y = 0; y < ny; y++) out[i * ny + y] = vol.voxels[x + y * nx + z * nx * ny];
  }
  return { width: ny, height: nz, data: out };
}

/** Coronal slice at y: columns = x, rows = z (superior at top). */
export function coronalSlice(vol: NiftiVolume, y: number): Slice2D {
  const [nx, ny, nz] = vol.dims;
  const out = new Float32Array(nx * nz);
  for (let i = 0; i < nz; i++) {
    const z = nz - 1 - i;
    const src = y * nx + z * nx * ny;
    out.set(vol.voxels.subarray(src, src + nx), i * nx);
  }
  return { width: nx, height: nz, data: out };
}

/** True when any voxel of axial slice k is > thr. */
export function sliceHasAbove(vol: NiftiVolume, k: number, thr = 0.5): boolean {
  const [nx, ny] = vol.dims;
  const n = nx * ny;
  const base = k * n;
  for (let i = 0; i < n; i++) if (vol.voxels[base + i] > thr) return true;
  return false;
}

export function sameShape(a: NiftiVolume, b: NiftiVolume): boolean {
  return a.dims[0] === b.dims[0] && a.dims[1] === b.dims[1] && a.dims[2] === b.dims[2];
}

/**
 * `find_center_slices` (dwi_visualization) / `_select_slices`
 * (pwi_visualization) — identical two-set heuristic in both modules:
 * every `interval`-th slice, plus the mask-containing (>0.5) slices (all of
 * them if there are <= nSlices, else the nSlices closest to depth//2).
 */
export function selectCenterSlices(mask: NiftiVolume, nSlices: number, interval: number): number[] {
  const depth = mask.dims[2];
  const center = Math.floor(depth / 2);
  const set2: number[] = [];
  for (let i = 0; i < depth; i += interval) set2.push(i);
  const nonzero: number[] = [];
  for (let k = 0; k < depth; k++) if (sliceHasAbove(mask, k)) nonzero.push(k);
  let set1 = nonzero;
  if (nonzero.length > nSlices) {
    // Python's `sorted(key=abs(i - center))` is stable, so ties keep ascending index order.
    set1 = nonzero
      .map((k, idx) => ({ k, idx }))
      .sort((a, b) => Math.abs(a.k - center) - Math.abs(b.k - center) || a.idx - b.idx)
      .slice(0, nSlices)
      .map((e) => e.k);
  }
  return Array.from(new Set([...set1, ...set2])).sort((a, b) => a - b);
}

// ---------------------------------------------------------------------------
// Intensity -> colour
// ---------------------------------------------------------------------------

export type Lut = Uint8Array; // 256 * 3

function buildLut(fn: (x: number) => [number, number, number]): Lut {
  const lut = new Uint8Array(256 * 3);
  for (let i = 0; i < 256; i++) {
    const [r, g, b] = fn(i / 255);
    lut[i * 3] = Math.round(r * 255);
    lut[i * 3 + 1] = Math.round(g * 255);
    lut[i * 3 + 2] = Math.round(b * 255);
  }
  return lut;
}

function segmentInterp(x: number, seg: [number, number][]): number {
  for (let i = 1; i < seg.length; i++) {
    if (x <= seg[i][0]) {
      const [x0, y0] = seg[i - 1];
      const [x1, y1] = seg[i];
      return x1 === x0 ? y1 : y0 + ((x - x0) / (x1 - x0)) * (y1 - y0);
    }
  }
  return seg[seg.length - 1][1];
}

/** matplotlib `_jet_data` segment table (LinearSegmentedColormap, N=256). */
const JET_R: [number, number][] = [[0, 0], [0.35, 0], [0.66, 1], [0.89, 1], [1, 0.5]];
const JET_G: [number, number][] = [[0, 0], [0.125, 0], [0.375, 1], [0.64, 1], [0.91, 0], [1, 0]];
const JET_B: [number, number][] = [[0, 0.5], [0.11, 1], [0.34, 1], [0.65, 0], [1, 0]];

export const GRAY_LUT: Lut = buildLut((x) => [x, x, x]);
export const JET_LUT: Lut = buildLut((x) => [segmentInterp(x, JET_R), segmentInterp(x, JET_G), segmentInterp(x, JET_B)]);

/** Finite min/max (imshow's default autoscale). */
export function finiteMinMax(data: Float32Array): [number, number] {
  let lo = Infinity;
  let hi = -Infinity;
  for (let i = 0; i < data.length; i++) {
    const v = data[i];
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (!isFinite(lo) || !isFinite(hi)) return [0, 1];
  return [lo, hi];
}

/** Sorted finite values, for repeated percentile lookups. */
export function sortedFinite(data: Float32Array): Float32Array {
  let n = 0;
  const tmp = new Float32Array(data.length);
  for (let i = 0; i < data.length; i++) if (Number.isFinite(data[i])) tmp[n++] = data[i];
  return tmp.subarray(0, n).sort();
}

/** `np.percentile(v, p)` (linear interpolation) on an already-sorted array. */
export function percentileSorted(sorted: Float32Array, p: number): number {
  const n = sorted.length;
  if (n === 0) return NaN;
  const pos = (p / 100) * (n - 1);
  const lo = Math.floor(pos);
  const hi = Math.min(n - 1, lo + 1);
  return sorted[lo] + (pos - lo) * (sorted[hi] - sorted[lo]);
}

/** k-th smallest (0-based) of a scratch array, in place (Hoare quickselect). */
function selectKth(a: Float32Array, k: number): number {
  let lo = 0, hi = a.length - 1;
  while (hi > lo) {
    const pivot = a[(lo + hi) >> 1];
    let i = lo, j = hi;
    while (i <= j) {
      while (a[i] < pivot) i++;
      while (a[j] > pivot) j--;
      if (i <= j) { const t = a[i]; a[i] = a[j]; a[j] = t; i++; j--; }
    }
    if (k <= j) hi = j;
    else if (k >= i) lo = i;
    else return a[k];
  }
  return a[k];
}

/**
 * `np.percentile(finite values, p)` without a full sort — O(n) quickselect,
 * for whole-volume windows (a 182x218x182 sort costs ~1s in the browser).
 */
export function percentileFinite(data: Float32Array, p: number): number {
  let n = 0;
  const tmp = new Float32Array(data.length);
  for (let i = 0; i < data.length; i++) if (Number.isFinite(data[i])) tmp[n++] = data[i];
  if (n === 0) return NaN;
  const a = tmp.subarray(0, n);
  const pos = (p / 100) * (n - 1);
  const lo = Math.floor(pos);
  const vLo = selectKth(a, lo);
  if (pos === lo || lo + 1 >= n) return vLo;
  // After selection everything right of lo is >= vLo; the next order statistic is their minimum.
  let vHi = Infinity;
  for (let i = lo + 1; i < n; i++) if (a[i] < vHi) vHi = a[i];
  return vLo + (pos - lo) * (vHi - vLo);
}

/**
 * `_display_range` (pwi_visualization) / `_percentile_window` (qc/rendering):
 * [p1, p2] percentiles of the finite values, falling back to min/max when
 * the percentile window is degenerate.
 */
export function displayRange(data: Float32Array, p1: number, p2: number): [number, number] {
  const s = sortedFinite(data);
  if (s.length === 0) return [0, 1];
  const lo = percentileSorted(s, p1);
  const hi = percentileSorted(s, p2);
  if (Number.isFinite(lo) && Number.isFinite(hi) && hi > lo) return [lo, hi];
  return [s[0], s[s.length - 1]];
}

/**
 * imshow(cmap, vmin, vmax) for one slice -> RGBA ImageData-compatible bytes.
 * Mirrors `Normalize` + `Colormap.__call__`: x*256 truncated, ==256 -> 255,
 * under/over clamp to the end colours, NaN -> fully transparent.
 * vmin == vmax maps everything to 0 (matplotlib's degenerate-norm behaviour).
 */
export function colorize(slice: Slice2D, lut: Lut, vmin: number, vmax: number): Uint8ClampedArray<ArrayBuffer> {
  const n = slice.width * slice.height;
  const out = new Uint8ClampedArray(n * 4);
  const span = vmax - vmin;
  for (let i = 0; i < n; i++) {
    const v = slice.data[i];
    if (Number.isNaN(v)) continue;
    let idx: number;
    if (span === 0) idx = 0;
    else {
      const x = ((v - vmin) / span) * 256;
      idx = x >= 256 ? 255 : x < 0 ? 0 : Math.floor(x);
      if (idx > 255) idx = 255;
    }
    out[i * 4] = lut[idx * 3];
    out[i * 4 + 1] = lut[idx * 3 + 1];
    out[i * 4 + 2] = lut[idx * 3 + 2];
    out[i * 4 + 3] = 255;
  }
  return out;
}

/**
 * Draws a colourised slice into a target rectangle (data extent fills the
 * rect, like an `aspect='equal'` imshow axes). Smoothing stands in for
 * matplotlib's 'antialiased' interpolation, which picks a hanning filter at
 * the <3x upsampling factors these figures use and 'nearest' above that.
 */
export function drawSlice(ctx: Canvas2D, rgba: Uint8ClampedArray<ArrayBuffer>, w: number, h: number, x: number, y: number, dw: number, dh: number, alwaysSmooth = false): void {
  const tile = createCanvas(w, h);
  const tctx = context2d(tile);
  tctx.putImageData(new ImageData(rgba, w, h), 0, 0);
  const upsample = Math.min(dw / w, dh / h);
  ctx.imageSmoothingEnabled = alwaysSmooth || upsample < 3;
  if (ctx.imageSmoothingEnabled) ctx.imageSmoothingQuality = alwaysSmooth ? "high" : "medium";
  ctx.drawImage(tile as CanvasImageSource, x, y, dw, dh);
}

/**
 * matplotlib imshow resampling for the report figures: interpolation
 * 'antialiased' at these (<3x) upsampling factors is Agg's hanning filter
 * (radius 1 -> cosine weights between the two nearest samples), applied to
 * the *data* before colour-mapping. That data-space stage is what gives the
 * native jet TTP panels their thin multi-colour rim at the brain edge and
 * turns isolated low voxels yellow rather than cyan, so it is reproduced
 * here instead of smoothing RGBA. >=3x upsampling is 'nearest'. A NaN
 * neighbour with non-zero weight yields NaN (transparent), as masked data
 * does in matplotlib.
 */
export function resampleSlice(slice: Slice2D, outW: number, outH: number): Slice2D {
  const { width: W, height: H, data } = slice;
  const out = new Float32Array(outW * outH);
  const nearest = Math.min(outW / W, outH / H) >= 3;
  // Per-axis source index + weight tables.
  const axis = (nOut: number, nIn: number) => {
    const i0 = new Int32Array(nOut), i1 = new Int32Array(nOut), w1 = new Float32Array(nOut);
    for (let o = 0; o < nOut; o++) {
      const u = ((o + 0.5) * nIn) / nOut - 0.5;
      if (nearest) {
        const n = Math.min(nIn - 1, Math.max(0, Math.round(u)));
        i0[o] = n; i1[o] = n; w1[o] = 0;
        continue;
      }
      const f0 = Math.floor(u);
      const f = u - f0;
      i0[o] = Math.min(nIn - 1, Math.max(0, f0));
      i1[o] = Math.min(nIn - 1, Math.max(0, f0 + 1));
      w1[o] = 0.5 - 0.5 * Math.cos(Math.PI * f);
    }
    return { i0, i1, w1 };
  };
  const ax = axis(outW, W);
  const ay = axis(outH, H);
  for (let y = 0; y < outH; y++) {
    const r0 = ay.i0[y] * W, r1 = ay.i1[y] * W, wy = ay.w1[y];
    for (let x = 0; x < outW; x++) {
      const c0 = ax.i0[x], c1 = ax.i1[x], wx = ax.w1[x];
      const a = data[r0 + c0], b = data[r0 + c1], c = data[r1 + c0], d = data[r1 + c1];
      const top = wx === 0 ? a : a + (b - a) * wx;
      const bot = wx === 0 ? c : c + (d - c) * wx;
      out[y * outW + x] = wy === 0 ? top : top + (bot - top) * wy;
    }
  }
  return { width: outW, height: outH, data: out };
}

/** Puts already output-resolution RGBA at (x, y) with normal compositing (NaN pixels stay see-through). */
export function blitRgba(ctx: Canvas2D, rgba: Uint8ClampedArray<ArrayBuffer>, w: number, h: number, x: number, y: number): void {
  const tile = createCanvas(w, h);
  context2d(tile).putImageData(new ImageData(rgba, w, h), 0, 0);
  ctx.drawImage(tile as CanvasImageSource, x, y);
}

// ---------------------------------------------------------------------------
// Contours (skimage.measure.find_contours)
// ---------------------------------------------------------------------------

/** A polyline in (row, col) image coordinates, pixel centres at integers. */
export type Contour = [number, number][];

/**
 * Marching squares at `level` on a display-order slice. Each edge crossing
 * is interpolated linearly like skimage; segments are chained into
 * polylines keyed on the shared edge they cross, so the returned point
 * count matches skimage's (`segments + 1`, closed loops repeat the start).
 */
export function findContours(slice: Slice2D, level = 0.5): Contour[] {
  const { width: W, height: H, data } = slice;
  if (W < 2 || H < 2) return [];
  const pts = new Map<number, [number, number]>();
  // Edge ids: horizontal edge (r,c)-(r,c+1) => 2*(r*W+c); vertical (r,c)-(r+1,c) => 2*(r*W+c)+1.
  const edgePoint = (id: number): [number, number] => {
    let p = pts.get(id);
    if (p) return p;
    const cell = id >> 1;
    const r = Math.floor(cell / W);
    const c = cell % W;
    const a = data[r * W + c];
    if (id & 1) {
      const b = data[(r + 1) * W + c];
      p = [r + (level - a) / (b - a), c];
    } else {
      const b = data[r * W + c + 1];
      p = [r, c + (level - a) / (b - a)];
    }
    pts.set(id, p);
    return p;
  };
  const adj = new Map<number, number[]>();
  const link = (e1: number, e2: number) => {
    let l = adj.get(e1);
    if (!l) adj.set(e1, (l = []));
    l.push(e2);
    let m = adj.get(e2);
    if (!m) adj.set(e2, (m = []));
    m.push(e1);
  };
  for (let r = 0; r < H - 1; r++) {
    for (let c = 0; c < W - 1; c++) {
      const ul = data[r * W + c] > level ? 1 : 0;
      const ur = data[r * W + c + 1] > level ? 1 : 0;
      const ll = data[(r + 1) * W + c] > level ? 1 : 0;
      const lr = data[(r + 1) * W + c + 1] > level ? 1 : 0;
      const code = ul | (ur << 1) | (ll << 2) | (lr << 3);
      if (code === 0 || code === 15) continue;
      const top = 2 * (r * W + c);
      const bottom = 2 * ((r + 1) * W + c);
      const left = 2 * (r * W + c) + 1;
      const right = 2 * (r * W + c + 1) + 1;
      switch (code) {
        case 1: case 14: link(top, left); break;
        case 2: case 13: link(top, right); break;
        case 4: case 11: link(left, bottom); break;
        case 8: case 7: link(right, bottom); break;
        case 3: case 12: link(left, right); break;
        case 5: case 10: link(top, bottom); break;
        // Saddles: skimage's default (fully_connected='low') keeps the
        // high-valued corners disconnected.
        case 6: link(top, left); link(right, bottom); break;
        case 9: link(top, right); link(left, bottom); break;
      }
    }
  }
  const contours: Contour[] = [];
  const visited = new Set<string>();
  const key = (a: number, b: number) => (a < b ? `${a}_${b}` : `${b}_${a}`);
  const walk = (start: number): Contour => {
    const chain: number[] = [start];
    let prev = -1;
    let cur = start;
    for (;;) {
      const next = (adj.get(cur) ?? []).find((n) => n !== prev && !visited.has(key(cur, n)))
        ?? (adj.get(cur) ?? []).find((n) => !visited.has(key(cur, n)));
      if (next === undefined) break;
      visited.add(key(cur, next));
      chain.push(next);
      prev = cur;
      cur = next;
      if (cur === start) break;
    }
    return chain.map(edgePoint);
  };
  // Open chains first (start from degree-1 edges at the image border), then loops.
  for (const [e, l] of adj) if (l.length === 1 && !visited.has(key(e, l[0]))) contours.push(walk(e));
  for (const [e, l] of adj) for (const n of l) if (!visited.has(key(e, n))) contours.push(walk(e));
  return contours;
}

/**
 * Plots contours over an image drawn in rect (x, y, w, h) for a slice of
 * size (sw, sh), clipped to the rect like matplotlib's axes clip box.
 */
export function strokeContours(
  ctx: Canvas2D,
  contours: Contour[],
  sw: number,
  sh: number,
  rect: { x: number; y: number; w: number; h: number },
  color: string,
  linewidthPt: number,
  alpha = 1
): void {
  if (contours.length === 0) return;
  const sx = rect.w / sw;
  const sy = rect.h / sh;
  ctx.save();
  ctx.beginPath();
  ctx.rect(rect.x, rect.y, rect.w, rect.h);
  ctx.clip();
  ctx.strokeStyle = color;
  ctx.globalAlpha = alpha;
  ctx.lineWidth = linewidthPt * PT;
  ctx.lineJoin = "round";
  ctx.lineCap = "square";
  ctx.beginPath();
  for (const c of contours) {
    for (let i = 0; i < c.length; i++) {
      const px = rect.x + (c[i][1] + 0.5) * sx;
      const py = rect.y + (c[i][0] + 0.5) * sy;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
  }
  ctx.stroke();
  ctx.restore();
}

/** Text extents for layout (falls back to em-based estimates if the browser lacks actualBoundingBox*). */
export function measureText(ctx: Canvas2D, text: string, fontPx: number, weight: "bold" | "normal"): { width: number; ascent: number; descent: number } {
  ctx.font = `${weight} ${fontPx}px ${FONT_FAMILY}`;
  const m = ctx.measureText(text);
  return {
    width: m.width,
    ascent: m.actualBoundingBoxAscent || fontPx * 0.76,
    descent: m.actualBoundingBoxDescent || fontPx * 0.24,
  };
}
